import React, { useCallback, useRef, useState } from 'react';
import { MapContainer, Marker, Polygon, Polyline, TileLayer, useMapEvents } from 'react-leaflet';
import { L } from '../../../utils/leafletIcons';
import { API, axios } from '../../../utils/api';
import { MAX_COMUNI, MAX_POLIGONI, MAX_VERTICI, nomeComune } from '../../../utils/profilo';
import { useTraduzioni } from '../../../i18n';
import ControlliMappa from './ControlliMappa';
import './mappa-zona.css';

// "La zona sulla mappa": invece di scrivere i comuni uno per uno, si disegna la zona con il dito o con il mouse.
// Si tocca «Disegna la zona» e poi la mappa, un punto alla volta; «Chiudi la zona» la chiude. Le zone possono essere
// più d'una, i punti si spostano trascinandoli e ogni zona si può togliere.
// I comuni coperti li calcola il server dai confini ISTAT (POST /api/zone/comuni, backend/zone.py) e si vedono come
// pillole: quelle che non servono si tolgono con la ✕ (e non tornano quando si ridisegna).
// Le tessere sono quelle di MappaCase (OpenStreetMap).
//
// I testi sono nella lingua del cliente (chiavi `profilo.zona.*` nei cinque dizionari). La stessa mappa si vede
// anche nella scheda dello staff, che sta fuori dal FornitoreLingua: lì useTraduzioni dà l'italiano da solo.
// Le pillole dei comuni portano lo stesso conto e le stesse parole dei comuni imparati dagli immobili
// (`profilo.ricerca.auto_*` in ProfiloCliente.jsx): sono lo stesso elenco, il campo `citta_ricerca`.

export const CENTRO = [45.58, 9.22]; // tra Milano e la Brianza, dove cercano quasi tutti i clienti
export const ZOOM = 11;

const iconaVertice = L.divIcon({ className: '', iconSize: [18, 18], iconAnchor: [9, 9], html: '<span class="vertice-zona"></span>' });
const COLORE = { color: '#E6C877', weight: 2, fillColor: '#E6C877', fillOpacity: 0.16 };

// I clic sulla mappa mentre si disegna (react-leaflet non passa gli eventi come proprietà: serve un figlio)
export const Clic = ({ attivo, onPunto }) => {
  useMapEvents({ click: (e) => { if (attivo) onPunto([e.latlng.lat, e.latlng.lng]); } });
  return null;
};

const arrotonda = (punto) => [Math.round(punto[0] * 1e5) / 1e5, Math.round(punto[1] * 1e5) / 1e5];

// La frase sotto la mappa: quanti comuni, quanti ne restano fuori, che cosa fare adesso (t e tp arrivano da chi
// disegna, così la frase è nella lingua del cliente)
export const notaZona = ({ t, tp, disegnando, punti, poligoni, comuni, troppi, caricamento, errore }) => {
  if (errore) return t('profilo.zona.nota_errore');
  if (disegnando) {
    return punti < 3 ? t('profilo.zona.nota_punti', { punti: tp('profilo.zona.plurali.punti', punti) })
      : t('profilo.zona.nota_ancora');
  }
  if (caricamento) return t('profilo.zona.nota_carico');
  if (!poligoni) return t('profilo.zona.nota_nessuna');
  if (!comuni) return t('profilo.zona.nota_nessun_comune');
  return troppi ? t('profilo.zona.nota_troppi', { max: MAX_COMUNI }) : t('profilo.zona.nota_pronta');
};

const MappaZona = ({ poligoni = [], comuni = [], onCambia, idMappa = 'zona' }) => {
  const { t, tp } = useTraduzioni();
  const [disegno, setDisegno] = useState(null); // i punti della zona che si sta disegnando, null = non si disegna
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState(false);
  const [troppi, setTroppi] = useState(false);
  const tolti = useRef(new Set()); // i comuni tolti a mano: non tornano quando si ricalcola la zona

  // I comuni coperti dalle zone, dal server; poi si salvano zone e comuni insieme
  const salva = useCallback(async (nuovi) => {
    if (!nuovi.length) {
      onCambia([], []);
      setTroppi(false);
      return;
    }
    setCaricamento(true);
    setErrore(false);
    try {
      const r = await axios.post(`${API}/zone/comuni`, { poligoni: nuovi });
      const coperti = (r.data?.comuni || []).map((c) => c.nome).filter((n) => !tolti.current.has(nomeComune(n)));
      setTroppi(Boolean(r.data?.troppi));
      onCambia(nuovi, coperti);
    } catch {
      setErrore(true);
      onCambia(nuovi, comuni); // la zona si salva lo stesso: i comuni restano quelli di prima
    } finally {
      setCaricamento(false);
    }
  }, [comuni, onCambia]);

  const aggiungiPunto = (punto) => setDisegno((p) => (p && p.length >= MAX_VERTICI ? p : [...(p || []), arrotonda(punto)]));
  const chiudiZona = () => {
    if (!disegno || disegno.length < 3) return;
    salva([...poligoni, disegno]);
    setDisegno(null);
  };
  const spostaPunto = (iZona, iPunto, punto) => salva(poligoni.map((zona, i) => (i !== iZona ? zona
    : zona.map((p, j) => (j !== iPunto ? p : arrotonda(punto))))));
  const togliZona = (iZona) => salva(poligoni.filter((_, i) => i !== iZona));
  const togliComune = (nome) => {
    tolti.current.add(nomeComune(nome));
    onCambia(poligoni, comuni.filter((c) => nomeComune(c) !== nomeComune(nome)));
  };

  const disegnando = disegno !== null;
  const pieno = poligoni.length >= MAX_POLIGONI;
  return (
    <div className="mappa-zona" data-testid={`mappa-zona-${idMappa}`}>
      <div className="testa-zona">
        <span>{t('profilo.zona.titolo')}</span>
        <div className="bottoni-zona">
          {!disegnando && (
            <button type="button" className="btn sm" onClick={() => setDisegno([])} disabled={pieno}
              title={pieno ? t('profilo.zona.max_zone', { max: MAX_POLIGONI }) : undefined}>
              {poligoni.length ? t('profilo.zona.un_altra') : t('profilo.zona.disegna')}
            </button>
          )}
          {disegnando && (
            <>
              <button type="button" className="btn sm p" onClick={chiudiZona} disabled={disegno.length < 3}>{t('profilo.zona.chiudi')}</button>
              <button type="button" className="btn sm" onClick={() => setDisegno(disegno.slice(0, -1))} disabled={!disegno.length}>{t('profilo.zona.togli_ultimo')}</button>
              <button type="button" className="btn sm" onClick={() => setDisegno(null)}>{t('comune.annulla')}</button>
            </>
          )}
          {!disegnando && poligoni.length > 0 && (
            <button type="button" className="btn sm" onClick={() => salva([])}>{t('profilo.zona.cancella')}</button>
          )}
        </div>
      </div>

      <div className={`tela-zona${disegnando ? ' disegno' : ''}`} data-testid="tela-zona">
        <MapContainer center={CENTRO} zoom={ZOOM} scrollWheelZoom={false} zoomControl={false} attributionControl={false} doubleClickZoom={!disegnando}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ControlliMappa />
          <Clic attivo={disegnando} onPunto={aggiungiPunto} />
          {poligoni.map((zona, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Polygon key={`zona-${i}`} positions={zona} pathOptions={COLORE} />
          ))}
          {!disegnando && poligoni.map((zona, i) => zona.map((punto, j) => (
            <Marker key={`p-${i}-${j}`} position={punto} icon={iconaVertice} draggable // eslint-disable-line react/no-array-index-key
              title={t('profilo.zona.punto_trascina', { n: j + 1, zona: i + 1 })}
              eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); spostaPunto(i, j, [lat, lng]); } }} />
          )))}
          {disegnando && disegno.length > 1 && <Polyline positions={disegno} pathOptions={COLORE} />}
          {disegnando && disegno.map((punto, j) => (
            // eslint-disable-next-line react/no-array-index-key
            <Marker key={`n-${j}`} position={punto} icon={iconaVertice} title={t('profilo.zona.punto', { n: j + 1 })} />
          ))}
        </MapContainer>
      </div>

      {poligoni.length > 0 && !disegnando && (
        <div className="elenco-zone" data-testid="elenco-zone">
          {poligoni.map((zona, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span className="zona-riga" key={`r-${i}`}>
              {poligoni.length > 1 ? t('profilo.zona.zona_n', { n: i + 1 }) : t('profilo.zona.la_zona')} ({tp('profilo.zona.plurali.punti', zona.length)})
              <button type="button" onClick={() => togliZona(i)} aria-label={t('profilo.zona.togli_zona', { n: i + 1 })}>✕</button>
            </span>
          ))}
        </div>
      )}

      {comuni.length > 0 && <p className="titolo-comuni">{tp('profilo.zona.plurali.comuni_ricerca', comuni.length)}</p>}
      <div className="pillole-comuni" data-testid="pillole-comuni" role="group"
        aria-label={tp('profilo.zona.plurali.comuni_ricerca', comuni.length)}>
        {comuni.map((c) => (
          <span className="pillola" key={c}>
            {c}
            <button type="button" onClick={() => togliComune(c)} aria-label={t('profilo.zona.togli_comune', { comune: c })}>✕</button>
          </span>
        ))}
      </div>
      <p className="nota-zona" role="status">
        {notaZona({ t, tp, disegnando, punti: disegno?.length || 0, poligoni: poligoni.length, comuni: comuni.length, troppi, caricamento, errore })}
      </p>
    </div>
  );
};

export default MappaZona;
